import {
  AlertTriangle,
  Check,
  Inbox,
  Loader2,
  MessageSquare
} from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { useTeamMembers } from "../../agents/useTeamMembers";
import { casePath } from "../../caseIde/routes";
import { PortalFirmAPI } from "../api/companiesApi";
import { refreshCompanies, useCaseRequests } from "../queries";
import type { Company, FirmCaseRequest } from "../types";
import { formatDate, withArticle } from "../utils";

const money = (n: number | null | undefined) =>
  n == null
    ? ""
    : n.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0
      });

/** What accepting creates, line by line (the dialog's right column). */
export const acceptLines = (
  company: Company,
  r: FirmCaseRequest,
  matterNo: string
): string[] => {
  const fee =
    r.estimate.amount != null
      ? `${money(r.estimate.amount)} ${
          r.estimate.basis === "hourly_capped" ? "cap" : "flat"
        }`
      : "Fee from your terms";
  const ids = [
    matterNo ? `Matter ${matterNo}` : "",
    r.client_matter_id ? `${company.name} ID ${r.client_matter_id}` : "",
    r.po ? `PO ${r.po}` : "",
    r.cost_center
  ].filter(Boolean);
  return [
    `Client record for ${r.employee_name}, linked to ${company.name}`,
    `${r.case_type}${r.entity ? ` with ${r.entity} as petitioner` : ""}`,
    ...(ids.length ? [ids.join(" · ")] : []),
    `${fee} · ${company.name} pays ${r.estimate.employer_share ?? 100}%${
      r.lock ? " (required by law)" : ""
    }`,
    `${r.rule_count} playbook ${
      r.rule_count === 1 ? "rule" : "rules"
    } on the case`,
    `Intake questionnaire to ${r.employee_email}`
  ];
};

const Field: FC<{ k: string; v: string; mono?: boolean }> = ({ k, v, mono }) =>
  v ? (
    <div className="flex gap-3 text-base">
      <span className="w-28 shrink-0 text-fg-3">{k}</span>
      <span className={mono ? "font-mono text-sm" : undefined}>{v}</span>
    </div>
  ) : null;

type DialogProps = {
  company: Company;
  request: FirmCaseRequest;
  onClose: () => void;
};

export const RequestReviewDialog: FC<DialogProps> = ({
  company,
  request: r,
  onClose
}) => {
  const navigate = useNavigate();
  const { members, me } = useTeamMembers();
  const [team, setTeam] = useState<string[]>(
    [company.responsible_user_id || me.uid].filter(Boolean)
  );
  const [matterNo, setMatterNo] = useState(r.suggested_matter_no ?? "");
  const [ack, setAck] = useState(false);
  const [mode, setMode] = useState<"review" | "decline" | "ask">("review");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sameName = (r.conflicts ?? []).filter((c) => c.kind === "same_name");
  const existing = (r.conflicts ?? []).find(
    (c) => c.kind === "existing_client"
  );
  const names = useMemo(
    () =>
      team
        .map((uid) => members.find((m) => m.uid === uid)?.label ?? "")
        .filter(Boolean)
        .join(", ") || "Pick the team",
    [members, team]
  );

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't update the request"
      );
    } finally {
      setBusy(false);
      void refreshCompanies();
    }
  };

  const accept = () =>
    run(async () => {
      const result = await PortalFirmAPI.accept(company.id, r.id, {
        team,
        matter_no: matterNo.trim() || undefined,
        acknowledge_conflicts: ack
      });
      toast(
        `${r.employee_name}'s case is open${
          result.matter_no ? ` as ${result.matter_no}` : ""
        }.${
          result.intake_sent ? " The intake questionnaire is on its way." : ""
        }`,
        {
          action: {
            label: "Open case",
            onClick: () =>
              navigate(
                casePath({
                  individualId: result.individual_id,
                  foreignNationalId: result.foreign_national_id
                })
              )
          }
        }
      );
    });
  const decline = () =>
    run(async () => {
      await PortalFirmAPI.decline(company.id, r.id, text.trim());
      toast(`Declined. ${r.requested_by.name} sees why in the portal.`);
    });
  const ask = () =>
    run(async () => {
      await PortalFirmAPI.ask(company.id, r.id, text.trim());
      toast(`Your question went to ${r.requested_by.name} in the portal`);
    });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-testid="request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Inbox className="size-4 text-fg-3" />
            Case request from {company.name} HR
            {r.approved_by ? (
              <Badge variant="secondary" className="font-normal">
                <Check /> Approved by {r.approved_by.name}
              </Badge>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            Sent by {r.requested_by.name}
            {r.created_at ? ` · ${formatDate(r.created_at)}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Field k="Employee" v={r.employee_name} />
            <Field k="Email" v={r.employee_email} />
            <Field k="Job title" v={r.job_title} />
            <Field k="Petitioner" v={r.entity} />
            <Field k="Worksite" v={r.worksite} />
            <Field k="Case type" v={r.case_type} />
            <Field k="Cost center" v={r.cost_center} mono />
            <Field k="PO" v={r.po} mono />
            <Field k={`${company.name} ID`} v={r.client_matter_id} mono />
            {r.notes ? (
              <p className="mt-1 rounded-md bg-fill-2 px-3 py-2 text-sm leading-4.5">
                {r.notes}
              </p>
            ) : null}
            {r.notes_thread.map((n) => (
              <p
                key={n.id}
                className="rounded-md border border-line-2 px-3 py-2 text-sm leading-4.5"
              >
                <span className="font-medium">{n.by.name}: </span>
                {n.text}
              </p>
            ))}
          </div>
          <div className="flex flex-col gap-2.5 rounded-md bg-fill-1 p-3">
            {mode === "review" ? (
              <>
                <span className="text-sm font-medium text-fg-3">
                  Accepting creates
                </span>
                {acceptLines(company, r, matterNo).map((line) => (
                  <span key={line} className="flex gap-2 text-base">
                    <Check className="mt-0.5 size-3.5 shrink-0 text-success" />
                    {line}
                  </span>
                ))}
                {existing && !busy ? (
                  <p className="text-sm text-fg-3">
                    {existing.name} is already a client ({existing.email}). The
                    case opens on that record.
                  </p>
                ) : null}
                <div className="flex items-center gap-2">
                  <Label className="w-16 text-sm">Team</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-7 max-w-60 justify-start truncate text-sm"
                      >
                        {names}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {members.map((m) => (
                        <DropdownMenuCheckboxItem
                          key={m.uid}
                          checked={team.includes(m.uid)}
                          onCheckedChange={(on) =>
                            setTeam((t) =>
                              on ? [...t, m.uid] : t.filter((x) => x !== m.uid)
                            )
                          }
                        >
                          {m.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="matter-no" className="w-16 text-sm">
                    Matter
                  </Label>
                  <Input
                    id="matter-no"
                    value={matterNo}
                    onChange={(e) => setMatterNo(e.currentTarget.value)}
                    className="h-7 w-40 font-mono text-sm"
                  />
                </div>
                {sameName.length ? (
                  <div
                    className="flex items-start gap-2 rounded-md border border-warn-line bg-warn-soft p-2 text-sm text-warn"
                    data-testid="conflict"
                  >
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                    <span className="flex-1">
                      Possible conflict: the firm already has{" "}
                      {sameName.map((c) => `${c.name} (${c.email})`).join(", ")}
                      . Check before accepting.
                    </span>
                    <Checkbox
                      checked={ack}
                      onCheckedChange={(v) => setAck(v === true)}
                      aria-label="I checked the conflict"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-fg-3">
                    No conflicts found on {r.employee_name}.
                  </p>
                )}
              </>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="request-text" className="text-sm">
                  {mode === "decline"
                    ? `Why? ${r.requested_by.name} sees this in the portal.`
                    : `Your question for ${r.requested_by.name}`}
                </Label>
                <Textarea
                  id="request-text"
                  rows={5}
                  value={text}
                  onChange={(e) => setText(e.currentTarget.value)}
                />
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="sm:justify-between">
          {mode === "review" ? (
            <>
              <Button
                variant="destructive"
                onClick={() => setMode("decline")}
                disabled={busy}
              >
                Decline
              </Button>
              <span className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setMode("ask")}
                  disabled={busy}
                >
                  <MessageSquare /> Ask HR a question
                </Button>
                <Button
                  variant="primary"
                  onClick={() => void accept()}
                  disabled={
                    busy || !team.length || (sameName.length > 0 && !ack)
                  }
                >
                  {busy ? (
                    <>
                      <Loader2 className="animate-spin" /> Opening the case…
                    </>
                  ) : (
                    "Accept and open case"
                  )}
                </Button>
              </span>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setMode("review")}>
                Back
              </Button>
              <Button
                variant={mode === "decline" ? "destructive" : "default"}
                disabled={busy || !text.trim()}
                onClick={() => void (mode === "decline" ? decline() : ask())}
              >
                {mode === "decline" ? "Decline request" : "Send question"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const STATUS_LABEL: Record<FirmCaseRequest["status"], string> = {
  pending_approval: "Waiting on their approver",
  pending_firm: "Waiting on you",
  accepted: "Accepted",
  declined: "Declined"
};

/** HR portal requests on the company's Employees tab. */
export const CaseRequestQueue: FC<{ company: Company }> = ({ company }) => {
  const { data } = useCaseRequests(company.id);
  const [params, setParams] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(null);
  const open = (data ?? []).filter(
    (r) => r.status === "pending_firm" || r.status === "pending_approval"
  );

  useEffect(() => {
    const id = params.get("request");
    if (id && data?.some((r) => r.id === id && r.status === "pending_firm")) {
      setOpenId(id);
      params.delete("request");
      setParams(params, { replace: true });
    }
  }, [data, params, setParams]);

  const current = data?.find((r) => r.id === openId);
  if (!open.length && !current) return null;
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-line-2 p-3"
      data-testid="request-queue"
    >
      <span className="text-base font-medium">Requests from HR</span>
      {open.map((r) => (
        <div key={r.id} className="flex items-center gap-3 text-base">
          <Inbox className="size-4 shrink-0 text-warn" />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate font-medium">
              {r.requested_by.name} asked for {withArticle(r.case_type)} for{" "}
              {r.employee_name}
            </span>
            <span className="truncate text-sm text-fg-3">
              {[r.job_title, r.cost_center].filter(Boolean).join(" · ")}
              {r.approved_by && r.approved_by.id !== r.requested_by.id
                ? ` · approved by ${r.approved_by.name}`
                : ""}
            </span>
          </span>
          <Badge variant="secondary" className="font-normal">
            {STATUS_LABEL[r.status]}
          </Badge>
          {r.status === "pending_firm" ? (
            <Button variant="primary" size="sm" onClick={() => setOpenId(r.id)}>
              Review
            </Button>
          ) : null}
        </div>
      ))}
      {current ? (
        <RequestReviewDialog
          company={company}
          request={current}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </div>
  );
};
